/* eslint-disable no-param-reassign */
import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CmcWebApi, CmcWebApiCommon } from '@cmctechnology/webinvest-api-client-web-trading';
import { getThemeConfig, Theme } from '@cmctechnology/webinvest-constants';
import {
  MARKETDATA_WS_STORE_NAME,
  createMarketDataWsStore,
  defaultMarketDataWsStore,
  marketDataWsMiddleware
} from '@cmctechnology/webinvest-api-client-market-data-ws';
import { Page } from './models/page';
import {
  ClientProfileSettingSave1,
  ClientRoles,
  ClientSettings,
  Country,
  StockLendingSettings,
  PendingOnlineApplicationsMode1,
  ProfileSetting,
  StockQuery,
  ClientProSubscriptionInfo
} from './types';
import { FRONTEND_STORE_NAME, createFrontendStore, defaultFrontendStore } from '@cmctechnology/webinvest-store-frontend';

export interface IClientStore {
  session: Partial<{
    authenticatedAt: number;
    timeoutMins: number;
    forceLogout: boolean;
  }>;
  crc?: number;
  theme?: Theme;
  isIntermediaryCompanyMember?: boolean;
  acceptEmail: boolean;
  name: Partial<{
    title: string;
    firstName: string;
    familyName: string;
    fullName: string;
  }>;
  contact: Partial<{
    email: string;
    mobile: string;
    homePhone: string;
    businessPhone: string;
    preferredContactMethod: string;
    notifyTradeEvents: boolean;
  }>;
  interceptors: CmcWebApi.Client.LogonInterceptorModel2[];
  trading: Partial<{
    pinEnabled: boolean;
    intermediaryCanOverrideBrokerage: boolean;
    notifyEvents: boolean;
    isInternationalAvailable: boolean;
    optionTradingLevel: number;
    algoFeeIsPaidByClientDefault: boolean;
  }>;
  settings: ClientSettings;
  roles: ClientRoles[];
  availableCountries?: Country[];
  page: Page;
  username: string;
  profileSettings: Partial<Record<ProfileSetting, any>>;
  intermediary: {
    pendingOnlineApplications: {
      hash: number;
      unfinished: unknown[];
      submitted: unknown[];
    };
    reportCount?: number;
    hasAdvisorFeeds?: boolean;
    canOverrideBrokerage?: boolean;
  };
  queries: {
    global: StockQuery[];
    client: StockQuery[];
  };
  siteVersion: string;
  machineName: string;
  isWsAuthenticated: boolean;
  recentStocks: string[];
  stockLendingSettings: StockLendingSettings;
  clientProSubscriptionInfo: ClientProSubscriptionInfo;
  dateEntered?: string;
}

export const defaultClientStore: IClientStore = {
  page: Page.LogIn,
  username: ``,
  session: {},
  name: {},
  contact: {},
  trading: {},
  settings: {},
  roles: [],
  interceptors: [],
  profileSettings: {},
  intermediary: {
    pendingOnlineApplications: {
      hash: 0,
      unfinished: [],
      submitted: []
    }
  },
  queries: {
    client: [],
    global: []
  },
  acceptEmail: false,
  siteVersion: ``,
  machineName: ``,
  isWsAuthenticated: false,
  recentStocks: [],
  stockLendingSettings: {},
  clientProSubscriptionInfo: {
    fetched: false,
    Success: false,
    EndDate: '',
    CancelToken: ''
  },
  dateEntered: undefined
};

export const SUCCESS = `Success`;
export const CLIENT_STORE_NAME = `client`;
export const createClientStore = (initialState: IClientStore) =>
  createSlice({
    name: CLIENT_STORE_NAME,
    initialState,
    reducers: {
      storeReset: () => defaultClientStore,
      authenticatedAtUpdated: (store: IClientStore, action: PayloadAction<number | undefined>) => {
        store.session.authenticatedAt = action.payload;
      },
      pageChanged: (store: IClientStore, action: PayloadAction<Page>) => {
        store.page = action.payload;
      },
      interceptorsRequired: (store: IClientStore, action: PayloadAction<CmcWebApi.Client.LogonInterceptorModel2[]>) => {
        store.interceptors = action.payload;
      },
      userInfoUpdated: (store: IClientStore, action: PayloadAction<{ username: string }>) => {
        store.username = action.payload.username;
      },
      detailsFetched: (
        store: IClientStore,
        action: PayloadAction<
          CmcWebApi.Client.ClientDetailsModel2 & {
            IntermediaryHasAdvisorFeeds: boolean;
            IntermediaryReportCount: number;
            SiteVersion: string;
            MachineName: string;
          }
        >
      ) => {
        store.crc = action.payload.ClientId;
        store.theme = action.payload.Theme as Theme;
        store.isIntermediaryCompanyMember = action.payload.IsIntermediaryCompanyMember;
        store.acceptEmail = action.payload.AcceptEmail;

        store.name.title = action.payload.Title;
        store.name.firstName = action.payload.FirstName;
        store.name.familyName = action.payload.Surname;
        store.name.fullName = `${action.payload.FirstName} ${action.payload.Surname}`;
        store.username = action.payload.LogonCode;

        store.contact.email = action.payload.EmailAddress;
        store.contact.mobile = action.payload.MobilePhone;
        store.contact.homePhone = action.payload.HomePhone;
        store.contact.businessPhone = action.payload.BusinessPhone;
        store.contact.preferredContactMethod = action.payload.PreferredContactMethod;
        store.contact.notifyTradeEvents = action.payload.NotifyTradeEvents;

        store.trading.pinEnabled = action.payload.TradingPinEnabled;
        store.trading.intermediaryCanOverrideBrokerage = action.payload.IntermediaryCanOverrideBrokerage;
        store.trading.notifyEvents = action.payload.NotifyTradeEvents;
        store.trading.isInternationalAvailable = action.payload.IsInternationalAvailable;
        store.trading.optionTradingLevel = action.payload.OptionTradingLevel;
        store.trading.algoFeeIsPaidByClientDefault = action.payload.AlgoFeeIsPaidByClientDefault;

        store.intermediary.canOverrideBrokerage = action.payload.IntermediaryCanOverrideBrokerage;
        store.intermediary.hasAdvisorFeeds = action.payload.IntermediaryHasAdvisorFeeds;
        store.intermediary.reportCount = action.payload.IntermediaryReportCount;

        store.session.timeoutMins = action.payload.SessionTimeout;

        store.settings = action.payload.Settings;

        store.siteVersion = action.payload.SiteVersion;
        store.machineName = action.payload.MachineName;
        store.dateEntered = action.payload.DateEntered?.toString();
      },
      rolesFetched: (store: IClientStore, action: PayloadAction<CmcWebApi.Client.ClientRolesModel1>) => {
        store.roles = action.payload.Roles as ClientRoles[];
      },
      profileSettingsFetched: (store: IClientStore, action: PayloadAction<CmcWebApi.Client.ClientPreferenceModel1[]>) => {
        action.payload.forEach((pref) => {
          const defaultProfileSettings = getThemeConfig(store.theme).defaultProfileSettings;
          const noValue = pref.Value === undefined || pref.Value === null || pref.Value === ``;
          store.profileSettings[pref.Key as ProfileSetting] = noValue && defaultProfileSettings[pref.Key] ? defaultProfileSettings[pref.Key] : pref.Value;
        });
      },
      stockLendingSettingsFetched: (store: IClientStore, action: PayloadAction<StockLendingSettings>) => {
        store.stockLendingSettings.ShowStockLendingInterceptor = action?.payload?.ShowStockLendingInterceptor ?? false;
      },
      clientProSubscriptionInfoFetched: (store: IClientStore, action: PayloadAction<ClientProSubscriptionInfo>) => {
        store.clientProSubscriptionInfo = { ...action.payload, fetched: true };
      },
      pendingOnlineApplicationFetched: (store: IClientStore, action: PayloadAction<{ response: PendingOnlineApplicationsMode1; hash: number }>) => {
        store.intermediary.pendingOnlineApplications.hash = action.payload.hash;
        store.intermediary.pendingOnlineApplications.submitted = action.payload.response.SubmittedApplications ?? [];
        store.intermediary.pendingOnlineApplications.unfinished = action.payload.response.UnfinishedApplications ?? [];
      },
      stockQueriesFetched: (store: IClientStore, action: PayloadAction<CmcWebApi.MarketInformation.StockFilterQueriesModel2>) => {
        if (action.payload.GlobalQueries) {
          store.queries.global = action.payload.GlobalQueries.map((query) => ({ id: query.QueryId, name: query.QueryName }));
        }
        if (action.payload.ClientSavedQueries) {
          store.queries.client = action.payload.ClientSavedQueries.map((query) => ({ id: query.QueryId, name: query.QueryName }));
        }
      },
      recentStocksFetched: (store: IClientStore, action: PayloadAction<CmcWebApi.RecentStocksGet2Handler.RecentStocksGet2Model>) => {
        store.recentStocks = (action?.payload?.Quotes ?? []).map(({ StockCode }) => StockCode);
      },
      unauthorised: (store: IClientStore) => {
        store.session.authenticatedAt = undefined;
        store.crc = undefined;
      },
      internationalCountriesFetched: (store: IClientStore, action: PayloadAction<CmcWebApi.Client.InternationalCountriesModel1>) => {
        store.availableCountries = action.payload.AvailableCountries as Country[];
      },
      selectedCountriesUpdated: (store: IClientStore, action: PayloadAction<string>) => {
        store.profileSettings.UiSelectedCountryIsoCode = action.payload;
      },
      clientDetailsUpdated: (
        store: IClientStore,
        action: PayloadAction<{ result: CmcWebApi.Client.ClientDetailsUpdateModel2; updateDetails: Partial<CmcWebApi.Client.ClientDetailsUpdateRequest2> }>
      ) => {
        if (action.payload.result.AcceptEmail?.Status === SUCCESS) {
          store.acceptEmail = action.payload.updateDetails.AcceptEmail!;
        }
      },
      clientFalconAccessDisabled: (store: IClientStore, action: PayloadAction<{ result: CmcWebApiCommon.Shared.OperationResultModel1 }>) => {
        if (action.payload.result.Success) {
          store.settings.FalconEnabled = false;
          store.settings.ForgerockEnabled = false;
          store.settings.MfaUIEnabled = false;
        }
      },
      clientProfileSettingsSaved: (
        store: IClientStore,
        action: PayloadAction<{ result: CmcWebApiCommon.Shared.OperationResultModel1; profileSettings: ClientProfileSettingSave1 }>
      ) => {
        if (action.payload.result.Success) {
          action.payload.profileSettings.Settings.forEach((pref) => {
            store.profileSettings[pref.Key as ProfileSetting] = pref.Value;
          });
        }
      },
      wsAuthenticated: (store: IClientStore, action: PayloadAction<boolean>) => {
        store.isWsAuthenticated = action.payload;
      },
      forceLogout: (store: IClientStore, action: PayloadAction<boolean>) => {
        store.session.forceLogout = action.payload;
      }
    }
  });

export const { actions, reducer } = createClientStore(defaultClientStore);

export const createStore = (initialStore: IClientStore = defaultClientStore) => {
  const clientStore = createClientStore(initialStore);
  return configureStore({
    reducer: {
      [CLIENT_STORE_NAME]: clientStore.reducer,
      [MARKETDATA_WS_STORE_NAME]: createMarketDataWsStore(defaultMarketDataWsStore).reducer,
      [FRONTEND_STORE_NAME]: createFrontendStore(defaultFrontendStore).reducer
    },
    // eslint-disable-next-line unicorn/prefer-spread
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(marketDataWsMiddleware({ debug: true }))
  });
};

export const ClientStore = createStore();

export type IStore = ReturnType<typeof ClientStore.getState>;
